import { createPortal } from 'react-dom';
import { forwardRef, useLayoutEffect, useState } from 'react';
import type { RefObject, ReactNode, HTMLAttributes } from 'react';

interface HeaderPopoverProps extends HTMLAttributes<HTMLDivElement> {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  align?: 'left' | 'right';
  children: ReactNode;
}

/**
 * Portals its children to document.body and positions them with `position: fixed`
 * relative to anchorRef's live bounding rect. Header controls sit inside a
 * horizontally-scrollable strip (overflow-x-auto), which per the CSS overflow spec
 * forces overflow-y to auto too — any plain `absolute` dropdown nested inside gets
 * clipped to that strip's height. Portaling out of the DOM subtree sidesteps that
 * clipping entirely, regardless of ancestor overflow.
 */
export const HeaderPopover = forwardRef<HTMLDivElement, HeaderPopoverProps>(
  function HeaderPopover({ anchorRef, open, align = 'right', className, style, children, ...rest }, ref) {
    const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null);

    useLayoutEffect(() => {
      if (!open) { setPos(null); return; }
      function update() {
        const el = anchorRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setPos(
          align === 'right'
            ? { top: r.bottom, right: window.innerWidth - r.right }
            : { top: r.bottom, left: r.left }
        );
      }
      update();
      window.addEventListener('scroll', update, true);
      window.addEventListener('resize', update);
      return () => {
        window.removeEventListener('scroll', update, true);
        window.removeEventListener('resize', update);
      };
    }, [open, anchorRef, align]);

    if (!open || !pos) return null;

    return createPortal(
      <div
        ref={ref}
        style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right, ...style }}
        className={className}
        {...rest}
      >
        {children}
      </div>,
      document.body,
    );
  },
);
